import { Controller } from "@hotwired/stimulus"

// Chip-based email input for adding external meeting guests (people
// without a ConsLog account). Type an email + Enter / comma / blur →
// a removable chip with a hidden `external_guest_emails[]` field.
// Validates format and de-dupes. meetings#create turns each into an
// email-only MeetingAttendee.
//
// On a recurring meeting each chip also carries a "This meeting / All
// meetings" scope picker (`external_guest_emails_scope[]`, aligned by
// position with the email field). It's hidden for one-off meetings and
// revealed/hidden live as the recurrence frequency changes. Default is
// "This meeting only".
export default class extends Controller {
  static targets = ["input", "chips", "error", "scope"]

  EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  connect() {
    this._freqEl = document.querySelector('select[name="recurrence[frequency]"]')
    if (this._freqEl) {
      this._onFreq = () => this._syncScopeVisibility()
      this._freqEl.addEventListener("change", this._onFreq)
    }
    this._syncScopeVisibility()
  }

  disconnect() {
    if (this._freqEl && this._onFreq) {
      this._freqEl.removeEventListener("change", this._onFreq)
    }
  }

  get isRecurring() {
    const v = this._freqEl ? this._freqEl.value : "none"
    return !!v && v !== "none"
  }

  _syncScopeVisibility() {
    if (!this.hasScopeTarget) return
    const show = this.isRecurring
    this.scopeTargets.forEach((s) => s.classList.toggle("hidden", !show))
  }

  keydown(event) {
    // Enter or comma commits the current value; backspace on an empty
    // input removes the last chip.
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault()
      this.commit()
    } else if (event.key === "Backspace" && this.inputTarget.value === "") {
      const last = this.chipsTarget.lastElementChild
      if (last) last.remove()
    }
  }

  commit() {
    const raw = this.inputTarget.value.replace(this.INVISIBLE, "").trim().replace(/[,;]+$/, "").trim()
    if (raw === "") return

    // Pasting a whole To: line works ("a@x.com; Conklin, Bob <bobc@mg.com>").
    // Each piece is tidied like the server does (EmailInput); pieces that
    // can't be read with one meaning stay in the box for the person to fix.
    const unreadable = []
    this._entries(raw).forEach(({ raw: piece, email }) => {
      if (!email || !this.EMAIL_RE.test(email)) {
        unreadable.push(piece)
        return
      }
      if (this._exists(email)) return
      this._addChip(email)
    })

    if (unreadable.length) {
      this._setError("That doesn't look like a valid email.")
      this.inputTarget.value = unreadable.join("; ")
    } else {
      this._setError(null)
      this.inputTarget.value = ""
    }
  }

  removeChip(event) {
    event.stopPropagation()
    event.currentTarget.closest("[data-chip]").remove()
  }

  focusInput() {
    this.inputTarget.focus()
  }

  // ─── private ──────────────────────────────────────────────────

  // Mirror of EmailInput (app/lib/email_input.rb) — keep the two in step.
  INVISIBLE = /[ ​-‍⁠﻿]/g
  EDGE_JUNK = /^[\s:;,.'"“”‘’()\[\]]+|[\s:;,.'"“”‘’()\[\]]+$/g
  LABEL = /^[^\s@:<>"]{1,30}:\s*/
  NAME_AND_ADDRESS = /^([\s\S]*?)\s*<\s*([^<>]*?)\s*>\s*$/

  _countAt(text) {
    return (text.match(/@/g) || []).length
  }

  // One box → [{ raw, email }]; email is null when the piece can't be read.
  _entries(raw) {
    if (this._countAt(raw) <= 1) return [this._clean(raw)]

    const pieces = raw.split(/[;,\n]+/).map((p) => p.trim()).filter(Boolean)
    // "Conklin, Bob <bobc@mg.com>" — a piece with no "@" is the start of a name.
    const merged = []
    let carry = null
    pieces.forEach((p) => {
      if (p.includes("@")) {
        merged.push(carry ? `${carry}, ${p}` : p)
        carry = null
      } else {
        carry = carry ? `${carry}, ${p}` : p
      }
    })
    if (carry) merged.push(carry)

    const split = []
    merged.forEach((p) => {
      if (this._countAt(p) <= 1) {
        split.push(p)
      } else if (p.includes(">")) {
        p.replace(/>\s*/g, ">\u0000").split("\u0000").filter(Boolean).forEach((q) => split.push(q))
      } else {
        const words = p.split(/\s+/)
        if (words.every((w) => w.includes("@"))) words.forEach((w) => split.push(w))
        else split.push(p) // "a@x.com bob smith@mg.com" — don't guess
      }
    })
    const seen = new Set()
    return split.map((p) => this._clean(p)).filter((e) => {
      const key = e.email || e.raw
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  }

  _clean(text) {
    let t = text.replace(this.INVISIBLE, "").trim().replace(this.EDGE_JUNK, "")
    const m = t.match(this.NAME_AND_ADDRESS)
    if (m) t = m[2]
    const address = this._tidyAddress(t)
    const ok = this._countAt(address) === 1 && !/[\s<>"]/.test(address)
    return { raw: text.trim(), email: ok ? address : null }
  }

  _tidyAddress(text) {
    let a = text.trim().replace(this.EDGE_JUNK, "")
    a = a.replace(this.LABEL, "").replace(this.EDGE_JUNK, "")
    return a.toLowerCase()
  }

  _exists(email) {
    return Array.from(
      this.chipsTarget.querySelectorAll('input[type="hidden"]')
    ).some((i) => i.value.toLowerCase() === email.toLowerCase())
  }

  _addChip(email) {
    const chip = document.createElement("span")
    chip.setAttribute("data-chip", "")
    chip.className =
      "inline-flex items-center gap-1.5 pl-3 pr-2 py-1 rounded-full text-[13px] font-medium bg-emerald-100 text-emerald-800"

    const label = document.createElement("span")
    label.textContent = email
    chip.appendChild(label)

    const hidden = document.createElement("input")
    hidden.type = "hidden"
    hidden.name = "external_guest_emails[]"
    hidden.value = email
    chip.appendChild(hidden)

    // Scope picker — only meaningful on a recurring meeting. Hidden for
    // one-off meetings; defaults to "This meeting only".
    const scope = document.createElement("select")
    scope.name = "external_guest_emails_scope[]"
    scope.setAttribute("data-email-chips-target", "scope")
    scope.setAttribute("aria-label", `Scope for ${email}`)
    scope.className =
      "cursor-pointer rounded-md border-0 bg-emerald-200/70 text-emerald-900 text-[13px] py-0 pl-2 pr-6 focus:ring-1 focus:ring-emerald-500"
    scope.add(new Option("This meeting", "this", true, true))
    scope.add(new Option("All meetings", "all"))
    if (!this.isRecurring) scope.classList.add("hidden")
    chip.appendChild(scope)

    const btn = document.createElement("button")
    btn.type = "button"
    btn.className = "flex items-center justify-center w-4 h-4 rounded-full text-emerald-600 hover:bg-emerald-200 hover:text-emerald-800 cursor-pointer leading-none shrink-0"
    btn.textContent = "×"
    btn.setAttribute("aria-label", `Remove ${email}`)
    btn.setAttribute("data-action", "click->email-chips#removeChip")
    chip.appendChild(btn)

    this.chipsTarget.appendChild(chip)
  }

  _setError(msg) {
    if (!this.hasErrorTarget) return
    if (msg) {
      this.errorTarget.textContent = msg
      this.errorTarget.classList.remove("hidden")
    } else {
      this.errorTarget.textContent = ""
      this.errorTarget.classList.add("hidden")
    }
  }
}
